import { createApi, createStore } from "effector";
import { persist } from "effector-storage/session";

import { resetAccountForLocationChange } from "../../account";

/**
 * Result of the Moving `quoteCalculate` call, read by branching-rules and by
 * the quoteCreate/callRequest contract conditions to decide what happens
 * after Special Items — see the OSE availability plan.
 */
export type QuoteCalculationResult = {
  hunks?: number;
  onSiteEstimateRequired?: boolean;
  callRequired?: boolean;
  interstateDestinationUnserviced?: boolean;
  /**
   * Whether any EST slot exists between today and the target date. Set by
   * the checkAvailabilityRange contract, which only fires when OSE is
   * required and the target date isn't today — see the OSE availability plan.
   */
  hasAvailabilityInRange?: boolean;
};

export const $quoteCalculation = createStore<QuoteCalculationResult | null>(
  null,
  // A calculate result for the old location is meaningless once the user's
  // zip resolves to a different one — same gating as $quote's own reset,
  // see entities/quote/model/store.ts.
).on(resetAccountForLocationChange, () => null);

// Persisted so a refresh mid-flow doesn't silently lose the OSE/track
// decision — without this, use-apply-track-skips.ts's activeTrack resets to
// null on refresh until additional-factors is resubmitted, incorrectly
// un-skipping steps that were correctly skipped a moment earlier.
persist<QuoteCalculationResult | null>({
  store: $quoteCalculation,
  key: "booking-v4-quote-calculation",
  def: null,
});

export const quoteCalculationApi = createApi($quoteCalculation, {
  // Merges rather than replaces — quoteCalculate and checkAvailabilityRange
  // each set a different subset of fields on this same store.
  set: (state, result: QuoteCalculationResult) => ({ ...state, ...result }),
  reset: () => null,
});
